import { Controller } from '@hotwired/stimulus'
import visibilityLoop from '../utils/visibility_loop'

// Walks a mockup through its states on a clock. The element carries
// `data-step` (0, 1, 2 …) and the stylesheet draws each state off it, so the
// choreography — a sheet sliding up, a cart bar appearing, an order landing —
// is plain CSS transitions between states rather than a stack of keyframes
// that have to agree on one timeline.
//
//   <div data-controller="demo-steps" data-demo-steps-hold-value="[1400, 800, 2600]">
//
// `hold` is how long each step stays, in ms; the last one is the pause before
// the loop replays. It only runs while on screen, and under reduced motion it
// lands on the last step and stays there.
//
// A shared mockup with its own steps can ride inside a longer scene on the
// same clock: mark it a `mirror` target with the outer step it starts at, and
// it gets `data-step` counted from there (and none before it):
//
//   <div class="giftcov2" data-demo-steps-target="mirror" data-demo-steps-from="10">
export default class extends Controller {
  static targets = ['mirror']

  static values = {
    hold: { type: Array, default: [2000] }
  }

  connect() {
    this.timer = null
    // A scene with no holds is a still — the reservations "No Cover Fees" card
    // is one. Stepping it divides by an empty timeline: `1 % 0` is NaN, the
    // hold for NaN is `undefined`, and `setTimeout(…, undefined)` fires on the
    // next tick, so the element ends up on `data-step="NaN"` inside a 0ms loop
    // that pegs the main thread for as long as the scene is on screen.
    if (this.holdValue.length === 0) return

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.show(this.holdValue.length - 1)
      return
    }
    this.stopLoop = visibilityLoop(this.element, {
      start: () => this.play(0),
      stop: () => this.pause()
    })
  }

  disconnect() {
    if (this.stopLoop) this.stopLoop()
  }

  play(step) {
    this.show(step)
    const next = (step + 1) % this.holdValue.length
    this.timer = setTimeout(() => this.play(next), this.holdValue[step])
  }

  pause() {
    clearTimeout(this.timer)
    this.timer = null
  }

  show(step) {
    this.element.dataset.step = step
    this.mirrorTargets.forEach((mirror) => {
      const from = Number(mirror.dataset.demoStepsFrom || 0)
      if (step >= from) {
        mirror.dataset.step = step - from
      } else {
        delete mirror.dataset.step
      }
    })
  }
}
